const DEFAULT_SELECTOR = 'a[href]';
const DEFAULT_EVENTS = ['pointerover', 'focusin', 'touchstart'];
const DIGITS = '0123456789';
const LOWERCASE_LETTERS = 'abcdefghijklmnopqrstuvwxyz';
const UPPERCASE_LETTERS = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ';
const DEFAULT_SPEED = 0.6;
const DEFAULT_SCRAMBLE_COUNT = 1;
const activeAnimations = new WeakMap();

export function attachScrambleOnHover(root, options = {}) {
    if (!root || prefersReducedMotion()) {
        return () => {};
    }

    const selector = options.selector || DEFAULT_SELECTOR;
    const events = options.events || DEFAULT_EVENTS;
    const animate = (event) => {
        const link = event.target?.closest?.(selector);

        if (!link || !root.contains(link) || isInternalPointerMove(event, link)) {
            return;
        }

        scrambleLinkText(link, options);
    };

    events.forEach((eventName) => root.addEventListener(eventName, animate, { passive: true }));

    return () => events.forEach((eventName) => root.removeEventListener(eventName, animate));
}

export function scrambleLinkText(link, options = {}) {
    return scrambleElementText(link, options);
}

export function scrambleElementText(element, options = {}) {
    if (!element || prefersReducedMotion()) {
        return false;
    }

    activeAnimations.get(element)?.cancel();

    const textNodes = getVisibleTextNodes(element);
    const characters = textNodes.flatMap(({ originalText }) => Array.from(originalText));
    const animatedIndexes = characters
        .map((character, index) => (isScrambleableCharacter(character) ? index : null))
        .filter((index) => index !== null);

    if (!animatedIndexes.length) {
        return false;
    }

    const animation = createScrambleAnimation(element, textNodes, characters, animatedIndexes, options);
    activeAnimations.set(element, animation);
    animation.start();

    return true;
}

function createScrambleAnimation(link, textNodes, characters, animatedIndexes, options) {
    const win = link.ownerDocument.defaultView;
    const speed = options.speed || DEFAULT_SPEED;
    const scrambleCount = options.scrambleCount || DEFAULT_SCRAMBLE_COUNT;
    const step = options.step || Math.max(Math.floor(animatedIndexes.length * 0.18), 1);
    const frameInterval = 1000 / (60 * speed);
    const countdowns = new Map(animatedIndexes.map((index) => [index, null]));
    let frameId = null;
    let previousTime = 0;
    let revealCursor = 0;
    let unlockDimensions = () => {};

    const render = () => {
        const output = characters.map((character, index) => {
            if (!countdowns.has(index)) {
                return character;
            }

            const countdown = countdowns.get(index);

            if (countdown === 0) {
                return character;
            }

            if (countdown > 0) {
                countdowns.set(index, countdown - 1);
            }

            return randomCharacterLike(character);
        });

        writeCharactersToTextNodes(textNodes, output);
    };

    const revealNextCharacters = () => {
        const nextCursor = Math.min(revealCursor + step, animatedIndexes.length);

        animatedIndexes.slice(revealCursor, nextCursor).forEach((index) => {
            countdowns.set(index, scrambleCount + randomInt(0, Math.ceil(scrambleCount / 2)));
        });

        revealCursor = nextCursor;
    };

    const tick = (time) => {
        if (!previousTime) {
            previousTime = time;
        }

        if (time - previousTime >= frameInterval) {
            revealNextCharacters();
            render();
            previousTime = time;
        }

        if (isComplete(countdowns)) {
            restoreTextNodes(textNodes);
            unlockDimensions();
            activeAnimations.delete(link);
            return;
        }

        frameId = win.requestAnimationFrame(tick);
    };

    return {
        start() {
            if (options.lockDimensions) {
                unlockDimensions = lockDimensions(link);
            }

            frameId = win.requestAnimationFrame(tick);
        },
        cancel() {
            if (frameId) {
                win.cancelAnimationFrame(frameId);
            }

            restoreTextNodes(textNodes);
            unlockDimensions();
            activeAnimations.delete(link);
        },
    };
}

function lockDimensions(element) {
    const rect = element.getBoundingClientRect();

    if (!rect.width || !rect.height) {
        return () => {};
    }

    const originalStyles = {
        boxSizing: element.style.boxSizing,
        display: element.style.display,
        height: element.style.height,
        width: element.style.width,
    };
    const computedDisplay = element.ownerDocument.defaultView.getComputedStyle(element).display;

    // Pin the rendered box while random characters with different glyph widths are shown.
    element.style.boxSizing = 'border-box';
    element.style.width = `${rect.width}px`;
    element.style.height = `${rect.height}px`;

    if (computedDisplay === 'inline' || !computedDisplay) {
        element.style.display = 'inline-block';
    }

    return () => {
        element.style.boxSizing = originalStyles.boxSizing;
        element.style.display = originalStyles.display;
        element.style.height = originalStyles.height;
        element.style.width = originalStyles.width;
    };
}

function getVisibleTextNodes(element) {
    const doc = element.ownerDocument;
    const nodeFilter = doc.defaultView.NodeFilter;
    const walker = doc.createTreeWalker(element, nodeFilter.SHOW_TEXT, {
        acceptNode(node) {
            const parentElement = node.parentElement;

            if (
                !node.textContent.trim() ||
                !parentElement ||
                parentElement.closest('script, style, svg, textarea, select, .sr-only, [aria-hidden="true"]')
            ) {
                return nodeFilter.FILTER_REJECT;
            }

            return nodeFilter.FILTER_ACCEPT;
        },
    });
    const nodes = [];
    let node = walker.nextNode();

    while (node) {
        nodes.push({ node, originalText: node.textContent });
        node = walker.nextNode();
    }

    return nodes;
}

function writeCharactersToTextNodes(textNodes, characters) {
    let offset = 0;

    textNodes.forEach(({ node, originalText }) => {
        const length = Array.from(originalText).length;

        node.textContent = characters.slice(offset, offset + length).join('');
        offset += length;
    });
}

function restoreTextNodes(textNodes) {
    textNodes.forEach(({ node, originalText }) => {
        node.textContent = originalText;
    });
}

function isComplete(countdowns) {
    return Array.from(countdowns.values()).every((countdown) => countdown === 0);
}

function isInternalPointerMove(event, link) {
    return event.type === 'pointerover' && event.relatedTarget && link.contains(event.relatedTarget);
}

function prefersReducedMotion() {
    return window.matchMedia?.('(prefers-reduced-motion: reduce)').matches === true;
}

function randomCharacterLike(character) {
    const characters = getReplacementCharacters(character);
    const originalIndex = characters.indexOf(character);
    let randomIndex = randomInt(0, characters.length - 2);

    if (randomIndex >= originalIndex) {
        randomIndex += 1;
    }

    return characters[randomIndex];
}

function getReplacementCharacters(character) {
    if (isUppercaseAsciiLetter(character)) {
        return UPPERCASE_LETTERS;
    }

    if (LOWERCASE_LETTERS.includes(character)) {
        return LOWERCASE_LETTERS;
    }

    return DIGITS;
}

function isScrambleableCharacter(character) {
    return isUppercaseAsciiLetter(character) || LOWERCASE_LETTERS.includes(character) || DIGITS.includes(character);
}

function isUppercaseAsciiLetter(character) {
    return UPPERCASE_LETTERS.includes(character);
}

function randomInt(min, max) {
    return Math.floor(Math.random() * (max - min + 1)) + min;
}
